// animatedLoops/catalog.js
//
// STOCK ANIMATED LOOP / GIF LIBRARY — catalog + self-contained SVG builder.
//
// Design source of truth: design-handoff/concepts/procedural-libraries/
// 02-animated-loop-library.html (state `loops`). 20 seamless, pure-CSS/SVG loops
// for Segment fills, Wheel wallpapers, and portraits — NO raster GIF, NO external
// asset. Gordon assumed animated GIFs were stocked; these are the token-cheap,
// self-contained equivalent that ships alongside the existing Giphy path.
//
// EACH loop is emitted as a single self-contained SVG whose motion is expressed
// with an internal <style> + CSS @keyframes. Rendered inside an <img> element the
// SVG runs its own animation in Blink / Gecko / WebKit, and its embedded
// `@media (prefers-reduced-motion: reduce)` block freezes it for users who ask —
// all without touching a single render site (it flows through the existing
// string-based wallpaper / portrait / media `src`).
//
// SEAMLESS BY CONSTRUCTION: pans translate by exactly one tile period, spins turn
// a full 360deg, hue cycles a full 360deg, and pulses/aurora use symmetric or
// alternate keyframes — so the closing frame equals the opening frame, no jump.
//
// ASPECT LAW: the SVG viewBox is a fixed 100x100 square clip; all motion lives on
// inner groups that are oversized and centered, so rotating / panning content
// circles inside identical image bounds and the frame never reshapes.

/* ----------------------------------------------------------------------------
 * Palettes (the "Loop colors" strip). Two-tone, brand-first, Violet default.
 * Each loop recolors from { bg, fg, fg2 } — the MOTION is untouched.
 * ------------------------------------------------------------------------- */
export const LOOP_PALETTES = [
  { id: "violet", name: "Violet", bg: "#EFE9FE", fg: "#8F38EF", fg2: "#9B5AF8" },
  { id: "grape", name: "Grape", bg: "#F3EEFF", fg: "#6D28D9", fg2: "#A78BFA" },
  { id: "sky", name: "Sky", bg: "#E6F4FF", fg: "#2563EB", fg2: "#60A5FA" },
  { id: "teal", name: "Teal", bg: "#E6FBF6", fg: "#0E9F8E", fg2: "#34D3C2" },
  { id: "cyan", name: "Cyan", bg: "#E6FBFF", fg: "#0891B2", fg2: "#38DDF2" },
  { id: "emerald", name: "Emerald", bg: "#E9FBEF", fg: "#059669", fg2: "#34D399" },
  { id: "amber", name: "Amber", bg: "#FFF6E1", fg: "#D97706", fg2: "#F1922B" },
  { id: "coral", name: "Coral", bg: "#FFEDE7", fg: "#EA580C", fg2: "#FB7A57" },
  { id: "rose", name: "Rose", bg: "#FFEBF3", fg: "#E11D67", fg2: "#F472A6" },
  { id: "slate", name: "Slate", bg: "#EEF2F7", fg: "#475569", fg2: "#94A3B8" },
];

export const DEFAULT_PALETTE_ID = "violet";

export const getPaletteById = (id) =>
  LOOP_PALETTES.find((p) => p.id === id) ||
  LOOP_PALETTES.find((p) => p.id === DEFAULT_PALETTE_ID);

// A palette can also be an arbitrary { bg, fg, fg2 } object (Segment / Wheel
// auto-match pills feed the live segment / wheel color in as `fg`).
export const paletteFromColor = (fg, { bg = "#F3EEFF", fg2 } = {}) => ({
  id: "custom",
  name: "Custom",
  bg,
  fg,
  fg2: fg2 || fg,
});

/* ----------------------------------------------------------------------------
 * SECURITY (NEM-3): colors are the only UNTRUSTED value interpolated into the
 * SVG markup (auto-match pills feed a live Segment/Wheel color straight through
 * `paletteFromColor`). `svgToDataUri` uses encodeURIComponent, which TRANSPORTS
 * but does NOT neutralize — a value like `x"/><animate .../>` survives the
 * round-trip and re-forms as attribute-breaking markup when the SVG is parsed.
 * We whitelist the color FORMAT at the single chokepoint every render path funnels
 * through (`resolveColors`), so nothing but a real color literal can ever reach an
 * attribute. Every whitelisted form (#hex, rgb()/rgba(), hsl()/hsla(), bare CSS
 * color keyword) is incapable of carrying <, >, ", ', & — closing the surface
 * defensively BEFORE the pills are wired (the only other dynamic values in the
 * markup are the static internal ids "al-*", never user-derived).
 * ------------------------------------------------------------------------- */
const HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB = /^rgba?\(\s*[0-9.,%\s/]+\)$/i;
const HSL = /^hsla?\(\s*[0-9.,%\s/]+\)$/i;
const KEYWORD = /^[a-z]+$/i; // CSS color keyword (red, transparent, …) — no markup chars

export const sanitizeColor = (value, fallback) => {
  if (typeof value !== "string") return fallback;
  const v = value.trim();
  if (HEX.test(v) || RGB.test(v) || HSL.test(v) || KEYWORD.test(v)) return v;
  return fallback;
};

/* ----------------------------------------------------------------------------
 * SVG plumbing
 * ------------------------------------------------------------------------- */
const VB = 100; // viewBox is 0 0 100 100 — the fixed clip (ASPECT LAW)

// Style block. For the animated variant this carries the keyframes plus a
// reduced-motion guard (defensive: effective only when the SVG is inlined into
// the DOM — an <img>-loaded SVG does not see the host's media queries, so the
// real reduced-motion respect lives in the React layer). The poster variant
// (empty `rules`) stays byte-static: no keyframes, no `animation:` at all.
const styleBlock = (rules) =>
  `<style>*{transform-box:fill-box}` +
  (rules
    ? `${rules}@media (prefers-reduced-motion:reduce){.al-anim{animation:none!important}}`
    : "") +
  `</style>`;

const svgWrap = (inner) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VB} ${VB}" ` +
  `preserveAspectRatio="xMidYMid slice" width="${VB}" height="${VB}">${inner}</svg>`;

/**
 * Turn an SVG string into an <img>-safe data URI. encodeURIComponent keeps the
 * markup intact across every engine (unlike raw base64 we can still read/diff it).
 */
export const svgToDataUri = (svg) =>
  `data:image/svg+xml,${encodeURIComponent(svg)}`;

/* ----------------------------------------------------------------------------
 * Motif drawers — one seamless tile of a repeating pattern, in user units.
 * Each returns { markup, tile } where `tile` is the seamless period.
 * ------------------------------------------------------------------------- */
const motifs = {
  dots: (c, r = 3.4, tile = 20) => ({
    tile,
    markup: `<circle cx="${tile / 2}" cy="${tile / 2}" r="${r}" fill="${c.fg}"/>`,
  }),
  dotGrid: (c, tile = 22) => ({
    tile,
    markup:
      `<circle cx="${tile / 2}" cy="${tile / 2}" r="2.4" fill="${c.fg}"/>` +
      `<circle cx="0" cy="0" r="1.4" fill="${c.fg2}"/>` +
      `<circle cx="${tile}" cy="${tile}" r="1.4" fill="${c.fg2}"/>`,
  }),
  plus: (c, tile = 22) => ({
    tile,
    markup: `<path d="M${tile / 2} 5 v10 M${tile / 2 - 5} 10 h10" stroke="${
      c.fg
    }" stroke-width="2.4" fill="none"/>`,
  }),
  confetti: (c, tile = 20) => ({
    tile,
    markup:
      `<path d="M${tile / 2} 5 v10 M${tile / 2 - 5} 10 h10" stroke="${
        c.fg
      }" stroke-width="2.2" fill="none"/>` +
      `<circle cx="2" cy="2" r="2" fill="${c.fg2}"/>` +
      `<circle cx="${tile - 2}" cy="${tile - 2}" r="2" fill="${c.fg2}"/>`,
  }),
  stripes: (c, tile = 24) => ({
    tile,
    markup: `<path d="M0 ${tile} L${tile} 0 M${-tile / 2} ${
      tile / 2
    } L${tile / 2} ${-tile / 2} M${tile / 2} ${tile * 1.5} L${tile * 1.5} ${
      tile / 2
    }" stroke="${c.fg}" stroke-width="9" fill="none"/>`,
  }),
  halfStripe: (c, tile = 24) => ({
    tile,
    markup: `<rect width="${tile / 2}" height="${tile}" fill="${c.fg}"/>`,
  }),
  wave: (c, tile = 40) => ({
    tile,
    markup: `<path d="M0 10 Q${tile / 4} 0 ${tile / 2} 10 T${tile} 10" stroke="${
      c.fg
    }" stroke-width="3" fill="none"/>`,
  }),
  chevron: (c, tile = 24) => ({
    tile,
    markup: `<path d="M0 ${tile * 0.7} L${tile / 2} ${tile * 0.3} L${tile} ${
      tile * 0.7
    } M0 ${tile * 1.2} L${tile / 2} ${tile * 0.8} L${tile} ${
      tile * 1.2
    } M0 ${tile * 0.2} L${tile / 2} ${-tile * 0.2} L${tile} ${
      tile * 0.2
    }" stroke="${c.fg}" stroke-width="3" fill="none"/>`,
  }),
};

/**
 * A seamless panning field: a pattern-filled oversized rect on a translating
 * group. Translating by exactly one tile period returns to an identical frame.
 */
const panLayer = (motif, c, { dx = 0, dy = 0, speed = 3.4, animated }) => {
  const { markup, tile } = motif;
  const pad = tile * 2;
  const anim =
    animated && (dx || dy)
      ? `.al-anim{animation:alpan ${speed}s linear infinite}` +
        `@keyframes alpan{to{transform:translate(${-dx * tile}px,${
          -dy * tile
        }px)}}`
      : "";
  return (
    `<rect width="${VB}" height="${VB}" fill="${c.bg}"/>` +
    `<defs><pattern id="al-p" width="${tile}" height="${tile}" ` +
    `patternUnits="userSpaceOnUse">${markup}</pattern></defs>` +
    `<g class="al-anim"><rect x="${-pad}" y="${-pad}" width="${VB + pad * 2}" ` +
    `height="${VB + pad * 2}" fill="url(#al-p)"/></g>` +
    styleBlock(anim)
  );
};

/* ----------------------------------------------------------------------------
 * Family renderers — each returns the inner SVG markup for one loop.
 * `animated` false => a static first-frame poster (used for grid tiles).
 * ------------------------------------------------------------------------- */
const families = {
  panX: (c, def, animated) =>
    panLayer(def.motif(c), c, { dx: 1, speed: def.speed, animated }),
  panY: (c, def, animated) =>
    panLayer(def.motif(c), c, { dy: 1, speed: def.speed, animated }),
  panXY: (c, def, animated) =>
    panLayer(def.motif(c), c, { dx: 1, dy: 1, speed: def.speed, animated }),

  spin: (c, def, animated) => {
    // NEM-2: the global `*{transform-box:fill-box}` makes `transform-origin:50px
    // 50px` resolve against each element's OWN fill bbox, not the 0-100 viewBox —
    // so a spin whose content bbox isn't anchored at (0,0) rotates off-centre.
    // `centered` spins override to `transform-box:view-box`, pinning the rotation
    // origin to the true viewport centre (50,50). Spin-rings deliberately omits
    // `centered` so it keeps its intentional off-centre orbit (deck note).
    const box = def.centered ? "transform-box:view-box;" : "";
    const anim = animated
      ? `.al-anim{${box}animation:alspin ${def.speed}s linear infinite;transform-origin:50px 50px}` +
        `@keyframes alspin{to{transform:rotate(360deg)}}`
      : "";
    // Oversized inner content (150 wide) centred in the 100 clip => rotates
    // inside identical bounds (ASPECT LAW).
    return (
      `<rect width="${VB}" height="${VB}" fill="${c.bg}"/>` +
      `<g class="al-anim">${def.draw(c)}</g>` +
      styleBlock(anim)
    );
  },

  hue: (c, def, animated) => {
    // Seamless hue shimmer via a TRANSLATING repeating gradient (transform, not
    // CSS `filter: hue-rotate`, because WebKit does not animate CSS filters in an
    // <img>-loaded SVG). The gradient repeats every 100 user units, so a 100u
    // translate returns to an identical frame.
    const anim = animated
      ? `.al-anim{animation:alhue ${def.speed}s linear infinite}` +
        `@keyframes alhue{to{transform:translateX(-100px)}}`
      : "";
    return (
      `<rect width="${VB}" height="${VB}" fill="${c.bg}"/>` +
      `<defs><linearGradient id="al-g" gradientUnits="userSpaceOnUse" ` +
      `x1="0" y1="0" x2="100" y2="40" spreadMethod="repeat">` +
      `<stop offset="0" stop-color="${c.fg}"/>` +
      `<stop offset=".5" stop-color="${c.fg2}"/>` +
      `<stop offset="1" stop-color="${c.fg}"/></linearGradient></defs>` +
      `<g class="al-anim"><rect x="-100" width="${VB * 3}" height="${VB}" ` +
      `fill="url(#al-g)"/></g>` +
      styleBlock(anim)
    );
  },

  aurora: (c, def, animated) => {
    const anim = animated
      ? `.al-anim{animation:alaur ${def.speed}s ease-in-out infinite alternate}` +
        `@keyframes alaur{to{transform:translateX(-100px)}}`
      : "";
    return (
      `<rect width="${VB}" height="${VB}" fill="${c.bg}"/>` +
      `<defs><linearGradient id="al-a" x1="0" y1="0" x2="1" y2="0">` +
      `<stop offset="0" stop-color="${c.fg2}"/><stop offset=".5" stop-color="${c.fg}"/>` +
      `<stop offset="1" stop-color="${c.fg2}"/></linearGradient></defs>` +
      `<g class="al-anim"><rect x="-100" width="${VB * 3}" height="${VB}" ` +
      `fill="url(#al-a)"/></g>` +
      styleBlock(anim)
    );
  },

  breathe: (c, def, animated) => {
    const anim = animated
      ? `.al-anim{transform-box:view-box;animation:albr ${def.speed}s ease-in-out infinite;transform-origin:50px 50px}` +
        `@keyframes albr{0%,100%{transform:scale(1)}50%{transform:scale(1.18)}}`
      : "";
    return (
      `<defs><radialGradient id="al-r" cx=".5" cy=".5" r=".5">` +
      `<stop offset="0" stop-color="${c.fg}"/><stop offset=".26" stop-color="${c.fg}"/>` +
      `<stop offset=".6" stop-color="${c.bg}"/></radialGradient></defs>` +
      `<rect width="${VB}" height="${VB}" fill="${c.bg}"/>` +
      `<circle class="al-anim" cx="50" cy="50" r="50" fill="url(#al-r)"/>` +
      styleBlock(anim)
    );
  },

  twinkle: (c, def, animated) => {
    const tile = 18;
    const anim = animated
      ? `.al-anim{animation:altw ${def.speed}s ease-in-out infinite}` +
        `@keyframes altw{0%,100%{opacity:.4}50%{opacity:1}}`
      : "";
    return (
      `<rect width="${VB}" height="${VB}" fill="${c.bg}"/>` +
      `<defs><pattern id="al-t" width="${tile}" height="${tile}" ` +
      `patternUnits="userSpaceOnUse"><circle cx="${tile / 2}" cy="${
        tile / 2
      }" r="3.4" fill="${c.fg}"/></pattern></defs>` +
      `<rect class="al-anim" width="${VB}" height="${VB}" fill="url(#al-t)"/>` +
      styleBlock(anim)
    );
  },

  sheen: (c, def, animated) => {
    const anim = animated
      ? `.al-anim{animation:alsw ${def.speed}s linear infinite}` +
        `@keyframes alsw{0%{transform:translateX(-140px) skewX(-18deg)}` +
        `100%{transform:translateX(240px) skewX(-18deg)}}`
      : "";
    return (
      `<rect width="${VB}" height="${VB}" fill="${c.bg}"/>` +
      `<defs><linearGradient id="al-s" x1="0" y1="0" x2="1" y2="0">` +
      `<stop offset="0" stop-color="${c.fg}" stop-opacity="0"/>` +
      `<stop offset=".5" stop-color="${c.fg}" stop-opacity=".55"/>` +
      `<stop offset="1" stop-color="${c.fg}" stop-opacity="0"/></linearGradient></defs>` +
      `<rect class="al-anim" x="30" y="-20" width="34" height="140" ` +
      `fill="url(#al-s)"/>` +
      styleBlock(anim)
    );
  },

  ripple: (c, def, animated) => {
    const ring = (delay) =>
      `<circle class="al-anim" cx="50" cy="50" r="20" fill="none" ` +
      `stroke="${c.fg}" stroke-width="3" style="animation-delay:${delay}s"/>`;
    const anim = animated
      ? `.al-anim{transform-box:view-box;animation:alrp ${def.speed}s ease-out infinite;transform-origin:50px 50px}` +
        `@keyframes alrp{0%{transform:scale(.2);opacity:0}18%{opacity:1}` +
        `100%{transform:scale(1.6);opacity:0}}`
      : "";
    return (
      `<rect width="${VB}" height="${VB}" fill="${c.bg}"/>` +
      ring(0) +
      ring(def.speed / 2) +
      styleBlock(anim)
    );
  },
};

/* ----------------------------------------------------------------------------
 * Spin drawers (oversized, centred content for the `spin` family)
 * ------------------------------------------------------------------------- */
const spinDrawers = {
  rings: (c) => {
    let rings = "";
    for (let r = 6; r <= 100; r += 8) {
      rings += `<circle cx="46" cy="46" r="${r}" fill="none" stroke="${
        r % 16 === 6 ? c.fg2 : c.fg
      }" stroke-width="2.2"/>`;
    }
    return `<g>${rings}</g>`;
  },
  colorWheel: (c) => {
    // Six-wedge colour wheel — rotating 360deg is seamless.
    const cols = [c.fg, c.fg2, c.fg, c.fg2, c.fg, c.fg2];
    let g = "";
    for (let i = 0; i < 6; i++) {
      const a0 = (i * 60 * Math.PI) / 180;
      const a1 = ((i + 1) * 60 * Math.PI) / 180;
      const x0 = 50 + 80 * Math.cos(a0);
      const y0 = 50 + 80 * Math.sin(a0);
      const x1 = 50 + 80 * Math.cos(a1);
      const y1 = 50 + 80 * Math.sin(a1);
      g += `<path d="M50 50 L${x0.toFixed(1)} ${y0.toFixed(1)} A80 80 0 0 1 ${x1.toFixed(
        1
      )} ${y1.toFixed(1)} Z" fill="${cols[i]}"/>`;
    }
    return `<g>${g}</g>`;
  },
  star: (c) => {
    let pts = "";
    for (let i = 0; i < 10; i++) {
      const rad = i % 2 === 0 ? 34 : 15;
      const a = (Math.PI / 5) * i - Math.PI / 2;
      pts += `${(50 + rad * Math.cos(a)).toFixed(1)},${(
        50 +
        rad * Math.sin(a)
      ).toFixed(1)} `;
    }
    return `<polygon points="${pts.trim()}" fill="${c.fg}"/>`;
  },
};

/* ----------------------------------------------------------------------------
 * THE CATALOG — 20 loops. Order mirrors the deck (Spin rings = slot 1, selected).
 * ------------------------------------------------------------------------- */
export const LOOPS = [
  { id: "spin-rings", name: "Spin rings", motion: "orbit 360°", family: "spin", speed: 12, draw: spinDrawers.rings },
  { id: "drift-dots", name: "Drift dots", motion: "pan →", family: "panX", speed: 3.4, motif: (c) => motifs.dots(c, 3.5, 20) },
  { id: "drift-diagonal", name: "Drift diagonal", motion: "pan ↘", family: "panXY", speed: 4.5, motif: (c) => motifs.dots(c, 3.5, 20) },
  { id: "barberpole", name: "Barberpole", motion: "pan ↓", family: "panY", speed: 1.9, motif: (c) => motifs.stripes(c, 24) },
  { id: "grid-pan", name: "Grid pan", motion: "pan ↘", family: "panXY", speed: 5.2, motif: (c) => motifs.dotGrid(c, 22) },
  { id: "waves-scroll", name: "Waves scroll", motion: "scroll →", family: "panX", speed: 3.2, motif: (c) => motifs.wave(c, 40) },
  { id: "chevron-march", name: "Chevron march", motion: "march ↓", family: "panY", speed: 2.6, motif: (c) => motifs.chevron(c, 24) },
  { id: "plus-drift", name: "Plus drift", motion: "pan →", family: "panX", speed: 4, motif: (c) => motifs.plus(c, 22) },
  { id: "spin-gradient", name: "Spin gradient", motion: "turn 360°", family: "spin", speed: 9, centered: true, draw: spinDrawers.colorWheel },
  { id: "hue-cycle", name: "Hue cycle", motion: "hue 360°", family: "hue", speed: 6 },
  { id: "aurora-shift", name: "Aurora shift", motion: "drift ⇄", family: "aurora", speed: 6 },
  { id: "breathe-blob", name: "Breathe blob", motion: "pulse", family: "breathe", speed: 3.4 },
  { id: "twinkle-dots", name: "Twinkle dots", motion: "fade", family: "twinkle", speed: 2.4 },
  { id: "diagonal-scroll", name: "Diagonal scroll", motion: "scroll ↘", family: "panX", speed: 2.2, motif: (c) => motifs.halfStripe(c, 24) },
  { id: "vertical-scroll", name: "Vertical scroll", motion: "scroll ↓", family: "panY", speed: 2.8, motif: (c) => motifs.wave(c, 20) },
  { id: "confetti-drift", name: "Confetti drift", motion: "pan ↘", family: "panXY", speed: 4.8, motif: (c) => motifs.confetti(c, 20) },
  { id: "spin-star", name: "Spin star", motion: "turn 360°", family: "spin", speed: 8, centered: true, draw: spinDrawers.star },
  { id: "sheen-sweep", name: "Sheen sweep", motion: "sweep →", family: "sheen", speed: 2.4 },
  { id: "ripple", name: "Ripple", motion: "ripple", family: "ripple", speed: 2.8 },
  { id: "slow-drift", name: "Slow drift", motion: "pan →", family: "panX", speed: 6, motif: (c) => motifs.dots(c, 3, 16) },
];

/**
 * Resolve a loop's colors from a palette (id, palette object, or {bg,fg,fg2}).
 */
export const resolveColors = (palette) => {
  const p =
    typeof palette === "string"
      ? getPaletteById(palette)
      : palette || getPaletteById(DEFAULT_PALETTE_ID);
  const d = getPaletteById(DEFAULT_PALETTE_ID); // safe fallbacks (violet)
  // NEM-3: sanitize every channel before it is interpolated into the SVG.
  return {
    bg: sanitizeColor(p.bg, d.bg),
    fg: sanitizeColor(p.fg, d.fg),
    fg2: sanitizeColor(p.fg2 || p.fg, d.fg2),
  };
};

/**
 * Build the SVG markup string for a loop.
 * @param {object} loop   one of LOOPS
 * @param {*} palette     palette id | palette obj | {bg,fg,fg2}
 * @param {object} opts   { animated?: boolean } — false => static poster
 */
export const renderLoopSvg = (loop, palette = DEFAULT_PALETTE_ID, opts = {}) => {
  const { animated = true } = opts;
  const c = resolveColors(palette);
  const fam = families[loop.family];
  if (!fam) throw new Error(`Unknown loop family: ${loop.family}`);
  return svgWrap(fam(c, loop, animated));
};

/**
 * Build the <img>-ready data URI for a loop.
 * Poster (animated:false) is used for the grid tiles (frozen-poster-on-tiles);
 * the animated variant is what gets stored on the fill / wallpaper / portrait.
 */
export const loopDataUri = (loop, palette = DEFAULT_PALETTE_ID, opts = {}) =>
  svgToDataUri(renderLoopSvg(loop, palette, opts));

export const getLoopById = (id) => LOOPS.find((l) => l.id === id);

/**
 * NEM-4: normalize an applied loop to the CANONICAL media value shape every other
 * MediaSelection source emits ({ id, src, url, title, thumbnail, type }) — see
 * librarySourceLogic.toInsertValue. Consumers that read `.url` / `.thumbnail` /
 * `.type` (preview thumbnails, type-branching) then get real values instead of
 * `undefined`. `thumbnail` is the STATIC poster (frozen first frame) so a preview
 * grid shows a still, not a moving image. `src`/`url` are the applied (animated
 * or reduced-motion poster) data URI the primary apply already used.
 *
 * @param {string} appliedUri  the data URI onSelect handed back (already reduced-
 *                             motion-aware)
 * @param {object} loop        the LOOPS entry
 * @param {object} meta        { loopId, palette } from onSelect's 3rd arg
 */
export const toLoopInsertValue = (appliedUri, loop, meta = {}) => {
  if (!loop) return null;
  const palette = meta.palette ?? DEFAULT_PALETTE_ID;
  return {
    id: loop.id,
    src: appliedUri,
    url: appliedUri,
    title: loop.name,
    thumbnail: loopDataUri(loop, palette, { animated: false }),
    type: "image",
  };
};
